import { ILocalEditContext } from '../interfaces/local-edit-context.interface';
import { ILocalEditEvents, LocalEditEvents } from '../interfaces/local-edit-events.interface';
import { ILocalEditService } from '../interfaces/local-edit-service.interface';
import {
    DocType,
    DocxServiceProtocol,
    EditCommonEvent,
    EditCommonEventHandlers,
    SaveType,
    MegaMediaServiceProtocol,
    IImageResult,
} from '@tencent/mega-sdk-protocols';
import {
    ContentStatusEvents,
    DocumentStatus,
    IContentStatusManager,
    ResourceStatus,
} from '@tencent/slide-sdk-model/src/manager/content-status-manager';
import { Channel, Client, Protocol } from '@tencent/mega-ipc-renderer';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { SlideFile } from '@tencent/slide-sdk-model';
import { ILogger, ISnackbar, SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import i18n from '@i18next/i18n';
import { createTimestampGenerator, formatError } from '@src/slide/domain/slide-app/utils/trace-format';
import { addUrlParam, removeURLParameter } from '@src/slide/infrastructure/module/independent/url/param-operation';
import { ILocalEditManager } from '../interfaces/local-edit-manager.interface';
import { DataConvertError } from '../interfaces/local-edit-data-converter.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { reaction } from 'mobx';
import { getXSRF } from '@src/slide/domain/slide-app/utils/cookie-utils';
import { IDocVerifierManager } from '@src/slide/domain/slide-app/core/interfaces/doc-verifier-manager.interface';

const MODULE = '[LocalEditManager]';
// 本地文件保存完成的 Event
const LocalEditDocumentSaved = 'local_edit_document_saved';

export abstract class BaseLocalEditManager implements ILocalEditManager {
    private client = Client.create({
        docx: Protocol<DocxServiceProtocol>(),
        media: Protocol<MegaMediaServiceProtocol>(),
    }, ENV.IS_MOBILE_LOCAL_EDIT);
    private editChannelPromise?: Promise<Channel<EditCommonEventHandlers>>;
    private isSavingNotice = false;
    // 加载文档完成后，首次变成 Sync 状态
    private isInitialSync = true;

    public constructor(
        protected logger: ILogger,
        protected context: ILocalEditContext,
        protected emitter: ILocalEditEvents,
        protected service: ILocalEditService,
        protected contentStatusManager: IContentStatusManager,
        protected snackbar: ISnackbar,
        protected docVerifierManager: IDocVerifierManager,
    ) {
        this.emitter.on(LocalEditEvents.SlideAppInitSuccess, () => {
            this.init();
            this.notifyResult();
        });
        this.emitter.on(LocalEditEvents.LoadDocumentAllChunksSuccess, () => {
            onSlideAppReady(() => setTimeout(() => this.saveSsrData(SaveType.AutoSave)));
        });
        this.emitter.on(LocalEditEvents.SlideAppDataError, (err) => {
            this.notifyResult(err as DataConvertError);
        });
        this.emitter.on(LocalEditEvents.LoadDocumentError, (err) => {
            this.notifyResult(err as DataConvertError);
        });
        this.emitter.on(LocalEditEvents.DocumentInfoChanged, this.onDocumentInfoChanged.bind(this));
        this.contentStatusManager.onDocumentStatusChange((status) => {
            this.logger.info(MODULE, 'DocumentStatus ->', status);
            this.updateContentStatus();
        });
        this.contentStatusManager.onResourceStatusChange((status) => {
            this.logger.info(MODULE, 'ResourceStatus ->', status);
            this.updateContentStatus();
        });

        let isTextUnsaved = false;
        reaction(
            () => this.getEditingStateManager().hasUnsavedTextContent,
            (isUnsaved) => {
                this.logger.info(MODULE, 'TextUnsavedState ->', isUnsaved);
                if (isUnsaved !== isTextUnsaved) {
                    this.contentStatusManager.emitter.emit(isUnsaved
                        ? ContentStatusEvents.ResourceSyncBegin
                        : ContentStatusEvents.ResourceSyncEnd);
                    isTextUnsaved = isUnsaved;
                }
            },
            { fireImmediately: true },
        );
    }

    public async save(saveType = SaveType.Save) {
        if (this.docVerifierManager.getIsReadOnly()) {
            // 编辑加密的文档，用户选择只读查看
            this.getSnackbBar().show({
                message: i18n.t('不支持保存[只读]文档'),
            });
            return;
        }
        this.logger.info(MODULE, '[save:recv]', '->', saveType);
        // 保存前主动触发失焦，确保多有编辑内容都被保存
        this.getEditorInstance()?.triggerBlur();
        await new Promise((resolve => setTimeout(resolve, 0)));
        return this.addSaveTask(saveType);
    }

    public saveAs() {
        return this.save(SaveType.SaveAs);
    }

    public downloadImage(remoteUrl: string): Promise<IImageResult> {
        return this.client.media.downloadImage({
            globalPadId: this.context.getGlobalPadId(),
            url: remoteUrl,
            xsrf: getXSRF(),
            localFilePath: this.context.getLocalFilePath(),
        });
    }

    protected useEditChannel = () => {
        if (!this.editChannelPromise) {
            this.editChannelPromise = this.client.docx.edit({
                globalPadId: this.context.getGlobalPadId(),
                docType: DocType.Slide,
            });
        }

        this.editChannelPromise.catch((err) => {
            this.logger.error(MODULE, 'failed to create edit channel:', formatError(err));
        });

        return this.editChannelPromise;
    };

    protected onDocumentInfoChanged(newPath: string, newTitle: string, newGlobalPadId: string) {
        this.logger.info(MODULE, '[document-info:change]', { newTitle, newPath, newGlobalPadId });
        SlideDataServices.SlideTitleManager()?.updateTitle(newTitle);
        // 修改另存为后新参数
        let newUrl = addUrlParam(location.href, {
            title: encodeURIComponent(newTitle),
            localFilePath: encodeURIComponent(newPath),
            globalPadId: encodeURIComponent(newGlobalPadId),
        });
        // 清除文档缓存标记
        if (newUrl.includes('recoveredType')) {
            newUrl = removeURLParameter(newUrl, 'recoveredType');
        }
        // 清除通过模板新建文档的模板路径
        if (newUrl.includes('templatePath')) {
            newUrl = removeURLParameter(newUrl, 'templatePath');
        }
        history.pushState({}, '', newUrl);
    }

    protected getEditorInstance() {
        return this.getAppServices().viewableServiceLayer.editorInstanceService.getEditorInstance();
    }

    protected getEditingStateManager() {
        return this.getAppServices().viewableServiceLayer.editingStateManager;
    }

    protected getAppServices() {
        return AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
    }

    private async updateContentStatus() {
        const documentStatus = this.contentStatusManager.getDocumentStatus();
        const resourceStatus = this.contentStatusManager.getResourceStatus();
        const editChannel = await this.useEditChannel();

        // 文档状态只有 Modified 和 Sync 状态才通知桌面端
        if (documentStatus !== DocumentStatus.Modified && documentStatus !== DocumentStatus.Sync) {
            return;
        }

        // 文档首次加载完成无需通知桌面端
        if (documentStatus === DocumentStatus.Sync && this.isInitialSync) {
            this.isInitialSync = false;
            // 模板新建文档首次打开需通知客户端文档变更
            if (this.context.getTemplatePath()) {
                editChannel.publish(EditCommonEvent.ContentModified, true);
            }
            return;
        }

        const isModified = documentStatus === DocumentStatus.Modified || resourceStatus !== ResourceStatus.Sync;
        this.logger.info(MODULE, 'ContentModified ->', isModified);
        editChannel.publish(EditCommonEvent.ContentModified, isModified);
    }

    private addSaveTask(saveType: SaveType) {
        if (saveType === SaveType.AutoSave) {
            // 桌面端请求自动保存时，只提供文档数据，不关心结果
            return this.processSaveTask(saveType);
        }

        this.getEditorInstance()?.triggerSave();

        if (this.contentStatusManager.getDocumentStatus() === DocumentStatus.Syncing && !this.isSavingNotice) {
            const duration = 1000;
            this.isSavingNotice = true;
            this.snackbar.show({
                message: i18n.t('正在保存文档，请稍候...'),
                autoClose: true,
                duration,
            });
            setTimeout(() => {
                this.isSavingNotice = false;
            }, duration);
        }

        const task = this.contentStatusManager.addDocumentSyncingTask(() => this.processSaveTask(saveType));
        task.catch((err: DataConvertError) => {
            this.logger.error(MODULE, 'failed to save document:', formatError(err));
        });
        return task;
    }

    private processSaveTask = async (saveType: SaveType): Promise<void> => {
        const timestamp = createTimestampGenerator();
        this.logger.info(MODULE, '[save:begin]', timestamp());
        const data = await this.serializePresentationData();
        this.logger.info(MODULE, '[save:serialize]', timestamp());
        await this.service.saveDocument(data, saveType);
        document.dispatchEvent(new CustomEvent(LocalEditDocumentSaved));
        // 每次保存成功后更新本地 ssr 数据，不关心返回结果
        this.saveSsrData(saveType);
    };

    private async serializePresentationDataByWorker(): Promise<string> {
        const SlideFileManager = await SlideDataServices.SlideFileManager.promise();
        const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
        const presentationCloneData = presentationStoreService.getPresentation().getWritableOwnedData();
        const instance = await SlideFileManager.recent();
        if (instance) {
            const slideFileService = instance.slideFile;
            const { data } = (await slideFileService.fromPresentationData(presentationCloneData) as any);
            SlideFileManager.recentBack(instance);
            return JSON.stringify(data);
        }
        return this.serializePresentationData();
    }

    private async serializePresentationData(): Promise<string> {
        const presentationStoreService = await SlideDataServices.SDKDataServices.PresentationStoreService.promise();
        const presentationCloneData = presentationStoreService.getPresentation().getWritableOwnedData();
        const data = SlideFile.fromPresentationData(presentationCloneData).getData();
        return JSON.stringify(data);
    }

    /**
     * 1. 分片数据全部加载完成，确保数据 ready 后，在下一个时间循环中回存首屏 ssr 数据
     * 2. 文档编辑后触发保存时，更新本地 ssr 数据
     */
    private saveSsrData(saveType: SaveType) {
        const ssrData = this.getSsrData();
        if (!ssrData) return;
        this.client.docx.saveSsrData({
            globalPadId: this.context.getGlobalPadId(),
            localFilePath: this.context.getLocalFilePath(),
            docType: DocType.Slide,
            title: this.context.getTitle(),
            data: ssrData,
        }, saveType);
    }

    protected abstract init();

    protected abstract getSnackbBar();

    /**
     * 文档加载结果 notify
     * 成功/失败 都需上报
     * @param err
     */
    protected abstract notifyResult(err?: DataConvertError);

    // TODO: 后续PC也支持本地ssr时，可统一在base中实现
    protected abstract getSsrData();
}
